import { formatCurrency } from '@if/core/utils'
import {
  ArrowPopover,
  Caption,
  FieldLabel,
  Icon,
  StrongValue,
  SubsectionTitle,
  TooltipIconButton,
  useMenu,
} from '@if/mui'
import { Box, Stack } from '@mui/material'
import { EPaymentMethod } from '@src/types/constants'
import { ArrowRight, Minus, Pencil, Plus, X } from 'lucide-react'
import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  EDIT_PAYMENT_PROVENANCE_FIELDS,
  EditPaymentProvenanceField,
  EditPaymentProvenanceValue,
  EditPaymentRowProvenance,
  EEditPaymentRowMark,
} from './EditPayments-provenance'
import { EditPaymentSchemaType } from './EditPayments-schema'

const MARK_COLUMN_SIZE = { xs: 40, lg: 32 }

export const MARK_COLUMN_SX = {
  width: MARK_COLUMN_SIZE,
  minWidth: MARK_COLUMN_SIZE,
  maxWidth: MARK_COLUMN_SIZE,
}

const MARK_ICON = {
  [EEditPaymentRowMark.Modified]: Pencil,
  [EEditPaymentRowMark.Added]: Plus,
  [EEditPaymentRowMark.Removed]: Minus,
} as const

const MARK_BUTTON_COLOR = {
  [EEditPaymentRowMark.Modified]: 'primary',
  [EEditPaymentRowMark.Added]: 'success',
  [EEditPaymentRowMark.Removed]: 'default',
} as const

const MARK_ICON_COLOR = {
  [EEditPaymentRowMark.Modified]: 'primary',
  [EEditPaymentRowMark.Added]: 'success',
  [EEditPaymentRowMark.Removed]: 'action',
} as const

const FIELD_LABEL_KEY = {
  effectiveDate: 'common.date',
  amount: 'loan.payment.amount',
  paymentMethod: 'loan.payment.pmtModeTitle',
  note: 'loan.payment.description',
  sendSmsReminder: 'loan.payment.editPayments.smsReminder',
} as const satisfies Record<EditPaymentProvenanceField, string>

interface ProvenanceLine {
  field: EditPaymentProvenanceField
  before?: EditPaymentProvenanceValue
  after?: EditPaymentProvenanceValue
}

interface EditPaymentRowMarkProps {
  payment: EditPaymentSchemaType
  provenance: EditPaymentRowProvenance | null
}

const EditPaymentRowMark = ({ payment, provenance }: EditPaymentRowMarkProps) => {
  const { t } = useTranslation()
  const [buttonProps, popoverProps, closePopover] = useMenu()

  const formatValue = (field: EditPaymentProvenanceField, value: EditPaymentProvenanceValue): string => {
    if (value === null || value === undefined || value === '') return t('common.none')
    if (field === 'amount') return formatCurrency(value as number)
    if (field === 'paymentMethod') return t(`enum.EPaymentMethod.${value as EPaymentMethod}`)
    if (field === 'sendSmsReminder')
      return value ? t('loan.payment.editPayments.smsOn') : t('loan.payment.editPayments.smsOff')
    return String(value)
  }

  const lines = useMemo<ProvenanceLine[]>(() => {
    if (!provenance) return []
    if (provenance.mark === EEditPaymentRowMark.Modified) return provenance.changes
    const source = provenance.mark === EEditPaymentRowMark.Added ? payment : provenance.openedAs
    if (!source) return []
    return EDIT_PAYMENT_PROVENANCE_FIELDS.map((field) =>
      provenance.mark === EEditPaymentRowMark.Added
        ? { field, after: source[field] }
        : { field, before: source[field] },
    )
  }, [payment, provenance])

  if (!provenance) return null

  const markLabel = t(`loan.payment.editPayments.marks.${provenance.mark}`)
  const title = t('loan.payment.editPayments.marks.title', {
    paymentNumber: payment.paymentNumber ?? '',
    mark: markLabel,
  })

  return (
    <>
      <TooltipIconButton {...buttonProps} title={title} size="small" color={MARK_BUTTON_COLOR[provenance.mark]}>
        <Icon icon={MARK_ICON[provenance.mark]} size="sm" />
      </TooltipIconButton>

      <ArrowPopover {...popoverProps}>
        <Stack spacing={1.5} sx={{ p: 2, minWidth: 260, maxWidth: 360 }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Icon icon={MARK_ICON[provenance.mark]} size="sm" color={MARK_ICON_COLOR[provenance.mark]} />
              <SubsectionTitle>{title}</SubsectionTitle>
            </Stack>
            <TooltipIconButton title={t('common.close')} size="small" onClick={closePopover}>
              <Icon icon={X} size="sm" />
            </TooltipIconButton>
          </Stack>

          <Caption>{t(`loan.payment.editPayments.marks.hint.${provenance.mark}`)}</Caption>

          {lines.map((line) => (
            <Box key={line.field}>
              <FieldLabel>{t(FIELD_LABEL_KEY[line.field])}</FieldLabel>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {'before' in line && (
                  <Caption sx={{ textDecoration: 'line-through' }}>{formatValue(line.field, line.before)}</Caption>
                )}
                {'before' in line && 'after' in line && <Icon icon={ArrowRight} size="sm" color="disabled" />}
                {'after' in line && <StrongValue>{formatValue(line.field, line.after)}</StrongValue>}
              </Stack>
            </Box>
          ))}
        </Stack>
      </ArrowPopover>
    </>
  )
}

export default memo(EditPaymentRowMark)
